import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { AuthService } from '@app/service/auth.service';
import { UserService } from '@data/services/user/user.service';
import { ToastMsgService } from '@shared/services/toastr/toast-msg.service';
import { Subject, first, takeUntil } from 'rxjs';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UserProfileComponent } from '@modal/header-modals/user-profile/user-profile.component';
import { LaunchdarklyService } from '@shared/services/LaunchDarkly/launchdarkly.service';
import { LDF_DISABLE_BULK_EMAIL, notificationTypes } from '@app/constants';
import { CommonService } from '@shared/services/common/common.service';

@Component({
  standalone: false,
  selector: 'app-configurable-settings',
  templateUrl: './configurable-settings.component.html',
  styleUrls: ['./configurable-settings.component.scss']
})
export class ConfigurableSettingsComponent implements OnInit {
  private _componentDestroyed$ = new Subject<boolean>;

  public notificationTypes = notificationTypes;
  public isLoading = true;
  public configurableSettingForm: FormGroup;
  public phoneNumberForm: FormGroup;
  public enhancedSOAForm: FormGroup = this._fb.group({
    'enhanced_soa_url': new FormControl('', [
      // value is not required and validate as URL
      (control) => {
        const value = control.value;
        if (!value) return null;
        // Simple URL regex (accepts .com, .org, .net, etc)
        const urlPattern = /^(https:\/\/){1}([\w\-]+\.)+[\w\-]{2,}(\/[\w\-._~:/?#[\]@!$&%'()*+,;=]*)?$/i;
        return urlPattern.test(value) ? null : { url: true };
      }
    ]),
    'enhanced_soa_acknowledged': new FormControl(false)
  });
  public communicationPreferencesForm: FormGroup;
  public batchNoticeForm: FormGroup;
  @ViewChild(MatTable) table: MatTable<any>;
  public message: any = {};
  public apiFail: boolean = false;
  public dataSource: MatTableDataSource<AbstractControl> = new MatTableDataSource<AbstractControl>();
  public displayedColumns: string[] = ['option', 'email', 'text'];
  public communicationPreferences: any[] = [];
  public batchNotice: any = null;
  public userID: any = this._authService.user_details.user_id
  public sspConfigurableSettings: boolean = false;
  public communicationPreferencesSettings: boolean = false;
  public communicationPreferencesSettingsUpdate: boolean = false;
  public usercommunicationPreferences: any;
  public textAppend: boolean;
  public processmessage: string;
  public disableTexting: boolean = false;
  public batchDisabled: boolean = false;
  public modifiedRecords: any[] = [];
  public ldlDisableBulkEmail: boolean = false;
  constructor(
    private _authService: AuthService,
    private _fb: FormBuilder,
    private _coomonService: CommonService,
    private _toastMessageSvc: ToastMsgService,
    private _userService: UserService,
    private dialogRef: MatDialogRef<ConfigurableSettingsComponent>,
    public dialog: MatDialog,
    private _launchDarklySrv: LaunchdarklyService,
  ) {
    this.configurableSettingForm = this._fb.group({
      //email reminder setting
      'sspr_configurable_settings': ['0'],
      //personal information update setting
      'sspup_configurable_settings': ['0']
    });
    this.phoneNumberForm = this._fb.group({
      'phone': new FormControl(''),
      'phone_type': new FormControl(''),
      "user_id": new FormControl('')
    });

    this.communicationPreferencesForm = this._fb.group({
      items: this._fb.array([])
    });
    this.dataSource.data = this.preferenceItems.controls;
    this.batchNoticeForm = this._fb.group({
      communication_notification_type_description: new FormControl(''),
      communication_notification_type_id:  new FormControl(''),
      communication_notification_type_name: new FormControl(''),
      communication_option_type_id: new FormControl(''),
      communication_preference_bit_email: new FormControl(false),
      communication_preference_bit_text: new FormControl(false),
      communication_preference_bit_inapp: new FormControl(false)
    });

    this._launchDarklySrv.getFlag(LDF_DISABLE_BULK_EMAIL)
        .pipe(takeUntil(this._componentDestroyed$))
        .subscribe((flag) => {
            this.ldlDisableBulkEmail = !!flag;
     });

  }

  ngOnInit(): void {
    this.userCommunicationPreference();
    this.selfServiceOptionSearch();
  }

  public tabClick(event: any) {
    switch (event.index) {
      case 0:
        this.sspConfigurableSettings = true;
        this.communicationPreferencesSettings = false;
        break;
      case 1:
        this.sspConfigurableSettings = false;
        this.communicationPreferencesSettings = true;
        break;
    }
  }

  public updateEnhancedSOA() {
    //call api to update the enhanced SOA acknowledgment
    let criteria: any = [{
      "self_service_config_option_type_id": "5",
      "self_service_config_option_bit": this.enhancedSOAForm.value.enhanced_soa_acknowledged ? '1' : '0',
      "self_service_config_hierarchy_type_id": "3",

    },{
      "self_service_config_option_type_id": "6",
	    "self_service_config_hierarchy_type_id": "3",
      "self_service_config_option_varchar": this.enhancedSOAForm.value.enhanced_soa_url ? this.enhancedSOAForm.value.enhanced_soa_url : ''
    }];
    this._userService.selfServiceConfigUpdate(this._authService.user_details.user_id, criteria).pipe(first()).subscribe({
      next: (response: any) => {
        this._toastMessageSvc.showSuccess('Settings updated.');
        this._coomonService.refreshEnhancedSOASetting(this.enhancedSOAForm.value.enhanced_soa_acknowledged);
        this._userService.setEnhSOASettings(this.enhancedSOAForm.value.enhanced_soa_acknowledged);
      },
      error: (err) => {
        this.apiFail = true;
        this.message = {
          type: 'alert',
          text: err.error.message
        };
        this.clearMessage(30000);
      }
    })

  }

  public loadUrl() {
    const url = this.enhancedSOAForm.value.enhanced_soa_url;
    if (url) {
      window.open(url, '_blank');
    }
  }

  public cancelEnhancedSOA() {

  }

  public sspSettingChange() {
    this.sspConfigurableSettings = true;
  }

  public selfServiceOptionSearch() {
    let payload: any = {
      "search_type": "simple",
      "criteria": {
        "user_id": {
          "type": "simple",
          "value": [this._authService.user_details.user_id]
        }
      }
    }
    this._userService.selfServiceConfig(this._authService.user_details.user_id, payload).pipe(first()).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        response.forEach((configOption: any) => {
          if (configOption.self_service_config_option_type_name === "Self-Service Portal send reminder emails") {
            //email reminder setting value
            this.configurableSettingForm.patchValue({ 'sspr_configurable_settings': configOption.self_service_config_option_bit })
          } else if (configOption.self_service_config_option_type_name === 'Self-Service Portal Personal Information Update') {
            //personal information update setting value
            this.configurableSettingForm.patchValue({
              sspup_configurable_settings:
                configOption.self_service_config_option_bit,
            });
          } else if (configOption.self_service_config_option_type_name === 'Enhanced SOA') {
            //enhanced SOA URL value
            this.enhancedSOAForm.patchValue({
              enhanced_soa_acknowledged:
                configOption.self_service_config_option_bit == '1'
            });
          } else if (configOption.self_service_config_option_type_name === 'Agent Calender URL') {
            //enhanced SOA URL value
            this.enhancedSOAForm.patchValue({
              enhanced_soa_url: configOption.self_service_config_option_varchar
            });
          }
        })
      },
      error: (err) => {
        this.isLoading = false;
        this.apiFail = true;
        this.message = {
          type: 'alert',
          text: err.error.message
        };
        this.clearMessage(30000);
      }
    })
  }

  public selfServiceConfigUpdate(ssprValue: number, sspupValue: number) {
    let criteria: any = [{
      "self_service_config_option_type_id": "3",//email reminder setting
      "self_service_config_option_bit": ssprValue ? ssprValue : '0'
    },
    {
      "self_service_config_option_type_id": "4",//personal information update setting
      "self_service_config_option_bit": sspupValue ? sspupValue : '0'
    }];
    this._userService.selfServiceConfigUpdate(this._authService.user_details.user_id, criteria).pipe(first()).subscribe({
      next: (response: any) => {
        this._toastMessageSvc.showSuccess('Settings updated.')
      },
      error: (err) => {
        this.apiFail = true;
        this.message = {
          type: 'alert',
          text: err.error.message
        };
        this.clearMessage(30000);
      }
    })
  }

  //Communication Preference
  private get preferenceItems(): FormArray {
    return this.communicationPreferencesForm.get('items') as FormArray;
  }

  public userCommunicationPreference() {
    this._userService.userCommunicationPreferencePermission(this._authService.user_details.user_id).pipe(first()).subscribe({
      next: (response: any) => {
        this.usercommunicationPreferences = response
        this.textAppend = this.usercommunicationPreferences.text_append;
        this.processmessage = this.usercommunicationPreferences.process_message;
        this.phoneNumberForm.patchValue({ phone_type: 'Mobile', phone: this.usercommunicationPreferences.phone, user_id: this.usercommunicationPreferences.user_id });
        this._userService.userCommunicationPreferenceOption(this._authService.user_details.user_id).pipe(first()).subscribe({
          next: (response: any) => {
            this.communicationPreferences = response.communication_preferences.filter((item: any) => item.communication_notification_type_id != this.notificationTypes.BATCH_EMAIL); // Exclude ID 10: Batch Email
            this.loadCommunicationPreferences();
            this.batchNotice = response.communication_preferences.find((item: any) => item.communication_notification_type_id == this.notificationTypes.BATCH_EMAIL); // Get only ID 10: Batch Email
            if(this.batchNotice) {
              this.loadBatchNotice();
            }
            this.renderCommunicationPreferences();

            setTimeout(() => {
              this.disableTexting = this.usercommunicationPreferences.disable_texting;
              this.batchDisabled = this.batchNoticeForm.value.communication_preference_bit_email;
            });
          },
          error: (err) => {
            this.message = {
              type: 'alert',
              text: err.error.message
            };
            this.clearMessage(30000);
          }
        })
      },
      error: (err) => {
        this.apiFail = true;
        this.message = {
          type: 'alert',
          text: err.error.message
        };
        this.clearMessage(30000);
      }
    })
  }

  private loadCommunicationPreferences() {
    this.communicationPreferences.forEach((item: any) => {
      this.preferenceItems.push(this._fb.group({
        communication_notification_type_description: [item.communication_notification_type_description],
        communication_notification_type_id: [item.communication_notification_type_id],
        communication_notification_type_name: [item.communication_notification_type_name],
        communication_option_type_id: [item.communication_option_type_id],
        communication_preference_bit_email: [item.communication_preference_bit_email === '1' ? true : false],
        communication_preference_bit_text: [item.communication_preference_bit_text === '1' ? true : false],
        communication_preference_bit_inapp: [item.communication_preference_bit_inapp === '1' ? true : false]
      }));
    });
  }

  private loadBatchNotice() {
    this.batchNoticeForm.patchValue({
      communication_notification_type_description: this.batchNotice?.communication_notification_type_description,
      communication_notification_type_id: this.batchNotice?.communication_notification_type_id,
      communication_notification_type_name: this.batchNotice?.communication_notification_type_name,
      communication_option_type_id: this.batchNotice?.communication_option_type_id,
      communication_preference_bit_email: this.batchNotice?.communication_preference_bit_email === '1' ? true : false,
      communication_preference_bit_text: this.batchNotice?.communication_preference_bit_text === '1' ? true : false,
      communication_preference_bit_inapp: this.batchNotice?.communication_preference_bit_inapp === '1' ? true : false
    });
  }

  private renderCommunicationPreferences() {
    if(this.table) {
      this.table.renderRows();
    }
  }

  public onCheckboxChange(rowId: number, field: 'communication_preference_bit_email' | 'communication_preference_bit_text' | 'communication_preference_bit_inapp', value: boolean) {

    this.communicationPreferencesSettingsUpdate = true;
    let record = this.modifiedRecords.find(item => item.communication_notification_type_id === rowId);
    if (!record) {
      let originalRecord = this.dataSource.filteredData.find(item => item.value.communication_notification_type_id === rowId)?.value;

      if (!originalRecord && this.batchNotice?.communication_notification_type_id === rowId) {
        originalRecord = this.batchNotice;
      }

      if (!originalRecord) return;

      record = { ...originalRecord };
      this.modifiedRecords.push(record);
    }
    record[field] = value == true ? '1' : '0';
  }

  public onBatchCheckboxChange(rowId: number, field: 'communication_preference_bit_email' | 'communication_preference_bit_text', value: any) {
    if(value == true) {
      this.dataSource.filteredData.forEach(item => {
        if (item.value.communication_notification_type_id !== this.notificationTypes.LA_PRO_UPDATES) {  //Only update if not LA Pro Updates
          item.get('communication_preference_bit_email')?.setValue(true);
          this.onCheckboxChange(item.value.communication_notification_type_id, 'communication_preference_bit_email', item.value.communication_preference_bit_email);
        }
      });
      this.renderCommunicationPreferences();
    }
    this.onCheckboxChange(rowId, field, value);
    this.batchDisabled = value === '1' || value === 1 || value === true;
  }


  public communicationPreferencesUpdate() {
    this.modifiedRecords.forEach((modifiedItem: { communication_notification_type_id: any; communication_preference_bit_email: any; communication_preference_bit_text: any;communication_preference_bit_inapp: any }) => {
      let originalItem = this.dataSource.filteredData.find(item => item.value.communication_notification_type_id === modifiedItem.communication_notification_type_id);

      if (!originalItem && this.batchNotice && this.batchNoticeForm.value.communication_notification_type_id === modifiedItem.communication_notification_type_id) {
        originalItem = this.batchNoticeForm;
      }

      if (originalItem) {
        if (
          originalItem.value.communication_preference_bit_email !== modifiedItem.communication_preference_bit_email ||
          originalItem.value.communication_preference_bit_text !== modifiedItem.communication_preference_bit_text ||
          originalItem.value.communication_preference_bit_inapp !== modifiedItem.communication_preference_bit_inapp
        ) {
          originalItem.value.communication_preference_bit_email = modifiedItem.communication_preference_bit_email == true ? '1' : '0';
          originalItem.value.communication_preference_bit_text = modifiedItem.communication_preference_bit_text == true ? '1' : '0';
          originalItem.value.communication_preference_bit_inapp = modifiedItem.communication_preference_bit_inapp == true ? '1' : '0';
        }
      }
    });
    this.modifiedRecords = [];

    let dataToPass = this.dataSource.filteredData.map((item: any) => {
      return {
        communication_notification_type_id: item.value.communication_notification_type_id,
        communication_notification_type_name: item.value.communication_notification_type_name,
        communication_option_type_id: item.value.communication_option_type_id,
        communication_preference_bit_email: item.value.communication_preference_bit_email == true ? '1' : '0',
        communication_preference_bit_text: item.value.communication_preference_bit_text == true ? '1' : '0',
        communication_preference_bit_inapp: item.value.communication_preference_bit_inapp == true ? '1' : '0'
      };
    });

    if(this.batchNotice) {
      dataToPass.push({
        communication_notification_type_id: this.batchNoticeForm.value.communication_notification_type_id,
        communication_notification_type_name: this.batchNoticeForm.value.communication_notification_type_name,
        communication_option_type_id: this.batchNoticeForm.value.communication_option_type_id,
        communication_preference_bit_email: this.batchNoticeForm.value.communication_preference_bit_email == true ? '1' : '0',
        communication_preference_bit_text: this.batchNoticeForm.value.communication_preference_bit_text == true ? '1' : '0',
        communication_preference_bit_inapp: this.batchNoticeForm.value.communication_preference_bit_inapp == true ? '1' : '0'
      });
    }

    let payload: any = {
      "user_id": this._authService.user_details.user_id,
      "communication_preferences": dataToPass
    }

    this._userService.userCommunicationPreferenceUpdate(this._authService.user_details.user_id, payload).pipe(first()).subscribe({
      next: (response: any) => {
        this._toastMessageSvc.showSuccess('Communication Preferences successfully updated.')
      },
      error: (err) => {
        this.apiFail = true;
        this.message = {
          type: 'alert',
          text: err.error.message
        };
        this.clearMessage(30000);
      }
    })
  }

  public openUserProfile() {
    this.dialog.open(UserProfileComponent, {
      width: '100%',
      autoFocus: false,
      data: {
        user_id: this.userID
      },
    });
    this.dialogRef.close(true);
  }

  /* clear notice message */
  public clearMessage(timer: number) {
    setTimeout(() => {
      this.message = this._toastMessageSvc.resetMessage();
    }, timer);
  }

  ngOnDestroy(): void {
    this._componentDestroyed$.next(true);
    this._componentDestroyed$.complete();
  }

}
